// restore-grid-focus-after-toggle.ts — PSTUDIO-1433

export interface PanelToggleFocusState {
  /** The panel's open state AFTER the toggle has been applied. */
  isPanelOpenAfterToggle: boolean;
  /** The cell the panel was editing, if any. */
  rowId: string | null | undefined;
  columnId: string | null | undefined;
  /**
   * Whether focus was already on a grid cell when the panel closed. Only the
   * Esc entrance can be in that state — the panel is a non-blocking sidebar, so
   * Esc can close it while the user is on a cell. Re-focusing there is not
   * merely redundant: the hand-back goes through `scrollToAndFocusCellById`,
   * which deselects every cell and row first, so it would silently drop a range
   * selection the user still has. Defaults to `false` for the in-editor
   * entrances (Shift+Enter), where focus is by definition inside the panel.
   */
  focusWasInsideGrid?: boolean;
}

/**
 * Whether closing the Expanded Cell View has to hand focus back to the grid.
 *
 * Shift+Enter from inside the panel editor closes the panel, which unmounts the
 * CodeMirror instance holding focus. The browser has nowhere to put focus and
 * drops it on `<body>`: the cell stays *selected*, but the grid's key handlers
 * never fire again, so arrows/Enter/typing all do nothing until the user
 * reaches for the mouse — a keyboard dead end reachable by keyboard alone.
 *
 * Only the CLOSE direction restores focus. On open the editor autofocuses
 * itself, and focusing the cell would immediately pull focus back out of it.
 */
export function shouldRestoreGridFocusAfterPanelToggle({
  isPanelOpenAfterToggle,
  rowId,
  columnId,
  focusWasInsideGrid = false,
}: PanelToggleFocusState): boolean {
  if (isPanelOpenAfterToggle) return false;
  if (focusWasInsideGrid) return false;
  return !!rowId && !!columnId;
}
